/**
 * Notification-API + Toast-Fallback (client.md §8). Toasts leben in einem eigenen,
 * nicht-persistenten Zustand-Store, damit Toasts.tsx unabhängig vom Aufrufer rendern kann.
 */
import { create } from "zustand";
import { useSettings } from "./state/settings";
import { useWorkspaces } from "./state/workspaces";
import { texte } from "./texte";

export type ToastItem = {
  id: string;
  title: string;
  text: string;
  info?: boolean;
  onOpen?: () => void;
};

type ToastState = {
  toasts: ToastItem[];
  push: (t: Omit<ToastItem, "id">) => void;
  dismiss: (id: string) => void;
};

export const useToasts = create<ToastState>((set) => ({
  toasts: [],
  push: (t) => {
    const id = Math.random().toString(36).slice(2);
    set((s) => ({ toasts: [...s.toasts, { ...t, id }] }));
    setTimeout(() => set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) })), 9000);
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((x) => x.id !== id) })),
}));

export function toast(title: string, text: string, info = false, onOpen?: () => void) {
  useToasts.getState().push({ title, text, info, onOpen });
}

export function requestNotificationPermission(): Promise<NotificationPermission> {
  if (!("Notification" in window)) return Promise.resolve("denied");
  return Notification.requestPermission();
}

/** Zeigt eine Browser-Notification, wenn erlaubt+aktiviert; sonst einen In-Page-Toast (§8). */
export function notify(workspace: string, title: string, text: string, opts?: { info?: boolean; onOpen?: () => void }) {
  const enabled = useSettings.getState().notifications;
  const permission = "Notification" in window ? Notification.permission : "denied";
  if (enabled && permission === "granted") {
    const n = new Notification(`${workspace}: ${title}`, { body: text });
    n.onclick = () => {
      window.focus();
      opts?.onOpen?.();
      n.close();
    };
  } else {
    toast(`${workspace}: ${title}`, `${text} ${texte.notify.clickToOpen}`, opts?.info, opts?.onOpen);
  }
}

/** Offene Frage → genau eine Notification, dedupliziert über notifiedQids (§8). */
export function notifyOpenQuestion(workspace: string, qid: string, question: string, onOpen?: () => void) {
  const entry = useWorkspaces.getState().byName[workspace];
  if (!entry || entry.notifiedQids.includes(qid)) return;
  useWorkspaces.getState().addNotifiedQid(workspace, qid);
  notify(workspace, texte.notify.agentQuestionTitle, question, { onOpen });
}

/** Statuswechsel nach „Budget erschöpft“/„Fertig“ — einmalig je Lauf (§8). */
const notifiedTerminal = new Set<string>();
export function notifyTerminalState(
  workspace: string,
  runId: string | undefined,
  kind: "budget" | "done",
  text: string,
  onOpen?: () => void,
) {
  const key = `${workspace}:${runId ?? "-"}:${kind}`;
  if (notifiedTerminal.has(key)) return;
  notifiedTerminal.add(key);
  notify(workspace, kind === "done" ? texte.notify.runFinishedTitle : texte.notify.agentQuestionTitle, text, {
    info: kind === "done",
    onOpen,
  });
}
